import React, { type PropsWithChildren, createContext, useContext } from "react"

import type { CompanyStatus } from "@/graphql/codegen/graphql"
import { COMPANY_TYPE } from "@/screens/Companies/constants"
import { useCompanyStore, useCompanyTypesStore } from "@/screens/Companies/stores"
import { extractChildrenIds } from "@/screens/CompanySettings/Accounting/utils/common"

interface PaymentTermsContextType {
  isCompanyVendor: boolean
  isCompanyCustomer: boolean
  companyStatus?: CompanyStatus
}

const PaymentTermsContext = createContext<PaymentTermsContextType | undefined>(undefined)

export const usePaymentTermsContext = (): {
  isCompanyVendor: boolean
  isCompanyCustomer: boolean
  companyStatus?: CompanyStatus
} => {
  const context = useContext(PaymentTermsContext)
  if (!context) {
    throw new Error("usePaymentTermsContext must be used within a PaymentTermsProvider")
  }
  return context
}

export const PaymentTermsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const companyTypes = useCompanyTypesStore((state) => state.companyTypes)
  const company = useCompanyStore((state) => state.company)

  const [isCompanyVendor, isCompanyCustomer] = [COMPANY_TYPE.VENDOR, COMPANY_TYPE.CUSTOMER]
    .map((type) => extractChildrenIds(companyTypes, [type]))
    .map((ids) => ids.some((id) => company?.types?.includes(id)))

  const value: PaymentTermsContextType = {
    isCompanyVendor,
    isCompanyCustomer,
    companyStatus: company?.status || undefined,
  }

  return <PaymentTermsContext.Provider value={value}>{children}</PaymentTermsContext.Provider>
}
